import { Injector, Injectable, InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';
import { Router } from '@angular/router';
import { AuthenticationResult, InteractionType, PopupRequest, RedirectRequest } from '@azure/msal-browser';
import { MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';

import { AccountService } from '../auth/account.service';
import { AuthServerProvider } from '../auth/auth-server-provider.service';
import { AppConfigService } from '../config/app-config.service';
import { clearKgChatStorage } from '../../knowledge-graph/auth-token';
import { StateStorageService } from '../auth/state-storage.service';

@Injectable({ providedIn: 'root' })
export class LoginService {
  private authService: MsalService | null = null;
  private msalGuardConfig: MsalGuardConfiguration | null = null;

  constructor(
    private accountService: AccountService,
    private authServerProvider: AuthServerProvider,
    private appConfig: AppConfigService,
    private injector: Injector,
    private router: Router,
    private stateStorageService: StateStorageService
  ) {
    if (this.appConfig.microsoftLogin) {
      this.authService = this.injector.get(MsalService);
      this.msalGuardConfig = this.injector.get<MsalGuardConfiguration>(MSAL_GUARD_CONFIG as InjectionToken<MsalGuardConfiguration>);
    }
  }

  login(): void {
    if (!this.msalGuardConfig || !this.authService) {
      return;
    }

    // KCS-565 Part 2: a staff Microsoft sign-in switches identity — clear any lingering candidate (temp user)
    // token first. Otherwise CandidateSessionService.isActive() (which keys off the stored candidate token,
    // and that token deliberately survives the main.ts wipe) would make the guards treat the freshly
    // logged-in staff session as a candidate and bounce it to /login. One browser holds one identity.
    this.authServerProvider.clearTokens();

    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      if (this.msalGuardConfig.authRequest) {
        this.authService.loginPopup({ ...this.msalGuardConfig.authRequest } as PopupRequest).subscribe((response: AuthenticationResult) => {
          this.authService!.instance.setActiveAccount(response.account);
        });
      } else {
        this.authService.loginPopup().subscribe((response: AuthenticationResult) => {
          this.authService!.instance.setActiveAccount(response.account);
        });
      }
    } else {
      if (this.msalGuardConfig.authRequest) {
        this.authService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
      } else {
        this.authService.loginRedirect();
      }
    }
  }

  logout(): void {
    if (!this.msalGuardConfig || !this.authService) {
      return;
    }

    // Defensive — localStorage.clear() below already wipes everything, but call
    // the helper explicitly so if that broad clear is ever narrowed, FAB chat
    // history still gets cleaned up on MSAL logout.
    this.accountService.signOut();
    this.stateStorageService.storeUrl(null);
    clearKgChatStorage();
    // Preserve the onboarding-tour "seen" flags across the broad clear — they must survive
    // logout so the welcome tour shows only on the very first login, not on every re-login.
    const preservedTour: Record<string, string> = {};
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith('tooltipTour.')) {
        const v = localStorage.getItem(k);
        if (v !== null) preservedTour[k] = v;
      }
    }
    localStorage.clear();
    Object.entries(preservedTour).forEach(([k, v]) => localStorage.setItem(k, v));
    const native = window as any;
    if (native.webkit?.messageHandlers?.logoutHandler) {
      native.webkit.messageHandlers.logoutHandler.postMessage('logout');
    }
    // KCS-565 Part 2: after Azure sign-out, land on the app's /login hub instead of '/'. Returning to '/'
    // re-triggers MsalGuard → an immediate Microsoft redirect, leaving no path to candidate login on a
    // shared device. We keep postLogoutRedirectUri at the (already-registered) ORIGIN — Azure forbids a
    // #fragment in registered redirect URIs, so we can't point it at /#/login directly. Instead drop a
    // one-shot marker that main.ts reads on the landing to route to /#/login (public, no MsalGuard). The
    // marker lives in sessionStorage so it survives the localStorage.clear() above and the round-trip.
    sessionStorage.setItem('lbPostLogoutToLogin', '1');
    const postLogout = window.location.origin;
    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      this.authService.logoutPopup({
        postLogoutRedirectUri: postLogout,
        mainWindowRedirectUri: postLogout,
      });
    } else {
      const activeAccount = this.authService.instance.getActiveAccount();
      this.authService.logoutRedirect({
        postLogoutRedirectUri: postLogout,
        account: activeAccount,
      });
    }
  }

  loginByLdap(credentials: { username: string; password: string }, callback?: Function): Promise<any> {
    const cb = callback || function () {};
    return new Promise((resolve, reject) => {
      this.authServerProvider.login(credentials).subscribe({
        next: (data) => {
          if (!data.isAuthenticated) {
            this.logoutByLdap();
            resolve(data);
            return cb(false);
          }
          this.authServerProvider.setTokens(data.token, data.refreshToken);
          this.accountService.identity(true).then(account => {
            resolve(data);
          });
          return cb();
        },
        error: (err) => {
          this.logoutByLdap();
          reject(err);
          return cb(err);
        }
      });
    });
  }

  // KCS-565: candidate fallback login with username (fake email) + password (QR camera unavailable). Hits Spring directly.
  loginByCredentials(username: string, password: string): Promise<any> {
    return this.completeTempLogin(this.authServerProvider.loginByCredentials(username, password));
  }

  // KCS-565 (Task 3b): step 1 — validate a scanned QR token and return the candidate's identity for
  // the "Log in as <name>?" confirmation. Does NOT store tokens. Resolves with { valid, name, phone, login }.
  resolveQr(token: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.authServerProvider.qrResolve(token).subscribe({ next: resolve, error: reject });
    });
  }

  // KCS-565 (Task 3b): step 2 — confirm the scanned candidate and complete login (stores access+refresh).
  loginByQr(token: string): Promise<any> {
    return this.completeTempLogin(this.authServerProvider.qrConfirm(token));
  }

  // Shared completion for temporary-user logins: store access + (stateless) refresh token, then resolve identity.
  // On failure, clean up WITHOUT navigating: logoutByLdap()'s router.navigate(['/login']) would tear down the
  // /candidate-login page (and its component-scoped toast) before the user can read the error message.
  private completeTempLogin(request: Observable<any>): Promise<any> {
    return new Promise((resolve, reject) => {
      request.subscribe({
        next: (data) => {
          if (!data.isAuthenticated) {
            this.clearFailedLoginAttempt();
            resolve(data);
            return;
          }
          this.authServerProvider.setTokens(data.token, data.refreshToken ?? '');
          this.accountService.identity(true).then(() => resolve(data));
        },
        error: (err) => {
          this.clearFailedLoginAttempt();
          reject(err);
        }
      });
    });
  }

  // A failed login attempt stored no tokens — defensive cleanup only, stay on the current page.
  private clearFailedLoginAttempt(): void {
    this.authServerProvider.clearTokens();
    this.accountService.signOut();
  }

  /**
   * Token-based logout (LDAP + KCS-565 candidate sessions). {@code redirectTo} defaults to the staff
   * {@code /login} hub; a candidate logout passes {@code /candidate-login} so the temp user lands back on
   * the candidate screen instead of being bounced to Microsoft by the /login route's loginRouteGuard.
   */
  logoutByLdap(redirectTo: string = '/login'): void {
    this.authServerProvider.clearTokens();
    this.accountService.signOut();
    this.stateStorageService.storeUrl(null); // don't carry a previous user's target URL into the next login
    clearKgChatStorage();
    this.router.navigate([redirectTo]);
  }
}
